<template>
    <div class="rounded-lg border border-slate-200 bg-white transition-all dark:border-slate-800 dark:bg-slate-950 flex flex-col relative overflow-hidden"
         :class="{
             'p-5': !subcategory.representative_image,
             'hover:shadow-lg hover:-translate-y-1': !subcategory.representative_image
         }">
        <Link
            :href="subcategory.url"
            class="block flex-1 relative z-10"
        >
            <!-- Image prompts: Full background image with overlay -->
            <div v-if="subcategory.representative_image"
                 class="absolute inset-0 pointer-events-none"
                 :style="{
                     backgroundImage: `url(${subcategory.representative_image.card_avif_url || subcategory.representative_image.card_url || subcategory.representative_image.url})`,
                     backgroundSize: 'cover',
                     backgroundPosition: 'center',
                     minHeight: '320px'
                 }">
            </div>

            <!-- Image prompts: Title with icon on left (like SubsubcategoryCard) -->
            <div v-if="subcategory.representative_image" class="p-3 min-h-[320px] flex flex-col justify-center relative z-10">
                <div class="bg-black bg-opacity-50 text-white px-2 py-2 rounded">
                    <!-- Title -->
                    <div class="flex items-start gap-x-2 mb-2">
                        <div class="h-5 flex items-center justify-center mt-0.5">
                            <component
                                v-if="iconComponent"
                                :is="iconComponent"
                                class="h-4 w-4 text-white"
                                :stroke-width="2.5"
                            />
                        </div>
                        <h3 class="font-medium text-sm">
                            {{ subcategory.name }}
                        </h3>
                    </div>

                    <!-- Subsubcategories list -->
                    <div class="text-xs">
                        <ul class="space-y-0.5">
                            <li v-for="subsubcat in displayedSubsubcategories" :key="subsubcat.id" class="flex justify-between items-center">
                                <Link
                                    :href="subsubcat.url"
                                    class="truncate flex-1 px-1 py-0.5 -mx-1 rounded hover:bg-white hover:bg-opacity-20 transition-all duration-200"
                                    @click.stop
                                >
                                    {{ subsubcat.name }}
                                </Link>
                            </li>
                        </ul>
<!--                        <div v-if="subcategory.subsubcategories?.length" class="mt-1">-->
<!--                            <Link-->
<!--                                :href="subcategory.url"-->
<!--                                class="text-white hover:text-blue-300 inline-block"-->
<!--                                @click.stop-->
<!--                            >-->
<!--                                Meer ›-->
<!--                            </Link>-->
<!--                        </div>-->
<!--                        <div v-else class="text-white text-opacity-80">-->
<!--                            Geen subcategorieën beschikbaar-->
<!--                        </div>-->
                    </div>
                </div>
            </div>

            <!-- Text prompts: Icon at top right (original layout) -->
            <div v-else class="mb-3 flex items-start justify-between">
                <div class="flex items-center gap-x-3 min-w-0">
                    <h3 class="text-lg font-medium text-slate-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-600">
                        <span>{{ subcategory.name }}</span>
                    </h3>
                </div>
                <!-- Icon positioned at top right -->
                <div class="shrink-0 self-start" :title="getIconTitle()">
                    <component
                        v-if="iconComponent"
                        :is="iconComponent"
                        class="h-12 w-12 text-gray-700"
                        :stroke-width="1.5"
                    />
                </div>
            </div>

            <!-- Text prompts: Show subsubcategories list -->
            <div v-if="!subcategory.representative_image" class="mt-2 flex-1 overflow-hidden relative">
                <template v-if="subcategory.subsubcategories?.length">
                    <div
                        v-for="subsubcat in displayedSubsubcategories"
                        :key="subsubcat.id"
                        class="flex justify-between items-center"
                    >
                        <Link
                            :href="subsubcat.url"
                            class="text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-600"
                        >
                            <span>{{ subsubcat.name }}</span>
                        </Link>
                    </div>
<!--                    <div v-if="subcategory.subsubcategories?.length" class="mt-2">-->
<!--                        <Link-->
<!--                            :href="subcategory.url"-->
<!--                            class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"-->
<!--                        >-->
<!--                            Meer ›-->
<!--                        </Link>-->
<!--                    </div>-->
                </template>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">
                    Geen subcategorieën beschikbaar
                </div>
            </div>
        </Link>

        <!-- Admin controls for icon matching -->
        <div v-if="page.props.auth?.user?.admin" class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div class="flex flex-col gap-2">
                <!-- Update Icon Form using Inertia -->
                <div class="mb-2">
                    <div class="flex items-center gap-2">
                        <input
                            type="text"
                            v-model="updateIconForm.icon"
                            class="flex-1 px-2 py-1 text-sm border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600"
                            placeholder="Icon name"
                        />
                        <button
                            @click="submitUpdateIconForm"
                            class="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {Link, useForm, usePage} from '@inertiajs/vue3'
import {computed, ref, onMounted} from 'vue'
import ClockIcon from '@/Components/Icons/ClockIcon.vue'
import {getIcon} from '@/Components/Icons/IconRegistry'

const props = defineProps({
    category: {
        type: Object,
        required: true
    },
    subcategory: {
        type: Object,
        required: true
    },
    showRecalculateButton: {
        type: Boolean,
        default: false
    }
})

// Get the page object from Inertia
const page = usePage()

// Ref for the icon component
const iconComponent = ref(null)
const iconName = ref(props.subcategory?.icon || '')
const isRecalculating = ref(false)
const recalculationError = ref('')
const recalculationSuccess = ref(false)
const updateSuccess = ref(false)

// Check if the current user is an admin
const isAdmin = computed(() => {
    return page.props.auth &&
           page.props.auth.user &&
           page.props.auth.user.admin;
})

// Create Inertia forms for icon management
const updateIconForm = useForm({
    // Initialize with empty string
    icon: ''
})

// Function to get icon title with icon name
const getIconTitle = () => {
    if (props.subcategory && props.subcategory.icon) {
        if (props.subcategory.icon.name) {
            return props.subcategory.icon.name;
        } else if (typeof props.subcategory.icon === 'string') {
            return props.subcategory.icon;
        }
    }
    return 'Icon';
}

// Function to submit the update icon form
const submitUpdateIconForm = () => {
    updateIconForm.post(`/categories/${props.subcategory.id}/update-icon`, {
        preserveScroll: true,
        onSuccess: () => {
            // Reload the page to show the updated icon
            window.location.reload()
        }
    })
}

// Load the icon component when the component is mounted
onMounted(async () => {
    // Debug: Log the subcategory object to see its structure
    // console.log('SubcategoryCard - Subcategory:', props.subcategory.name)
    // console.log('SubcategoryCard - Icon data:', props.subcategory?.icon)
    // console.log('SubcategoryCard - Has representative_image:', !!props.subcategory.representative_image)

    // Set the icon name in the form
    if (props.subcategory?.icon) {
        // The icon is an object with a name property
        if (props.subcategory.icon.name) {
            updateIconForm.icon = props.subcategory.icon.name
        } else if (typeof props.subcategory.icon === 'string') {
            updateIconForm.icon = props.subcategory.icon
        } else {
            updateIconForm.icon = ''
        }
    } else {
        updateIconForm.icon = ''
    }

    // Load the icon for the subcategory if available
    if (props.subcategory && props.subcategory.icon) {
        try {
            // The icon component name is stored in icon.componentName
            if (props.subcategory.icon.componentName) {
                // console.log('SubcategoryCard - Loading icon:', props.subcategory.icon.componentName)
                iconComponent.value = await getIcon(props.subcategory.icon.componentName)
                // console.log('SubcategoryCard - Icon loaded successfully')
            } else if (props.subcategory.icon_component) {
                // Fallback to old structure if it exists
                // console.log('SubcategoryCard - Loading icon (fallback):', props.subcategory.icon_component)
                iconComponent.value = await getIcon(props.subcategory.icon_component)
            } else {
                console.log('SubcategoryCard - No icon component name found in:', props.subcategory.icon)
            }
        } catch (error) {
            console.error('SubcategoryCard - Error loading icon:', error)
        }
    } else {
        // console.log('SubcategoryCard - No icon data available')
    }
})

const displayedSubsubcategories = computed(() => {
    return props.subcategory.subsubcategories?.slice(0, 5) || []
})

const totalSubsubcategories = computed(() => {
    return props.subcategory.subsubcategories?.length || 0
})

const hasMoreSubsubcategories = computed(() => {
    const total = totalSubsubcategories.value
    // console.log(`SubcategoryCard - ${props.subcategory.name}: ${total} subsubcategories, showing ${displayedSubsubcategories.value.length}`)
    return total > 5
})


</script>
